// src/utils/dates.ts
//
// Tolerantní práce s datem napříč daty aplikace (krok 8 redesignu, backlog #23).
//
// V databázi se potkává několik zápisů téhož:
//   - races.date: "YYYY-MM-DD" (řetězec; starší dokumenty Firestore Timestamp)
//   - payments.date: "DD.MM.YYYY" z banky (Cloud Function), "2. 10. 2026"
//     z ruční platby (toLocaleDateString("cs-CZ") – s mezerami, bez nul),
//     nově ISO "YYYY-MM-DD"
//   - registrations.registeredAt, users.zpValidity, races.finishedAt:
//     Firestore Timestamp / { seconds, nanoseconds }
//   - Date, number (milisekundy od epochy)
// Tady je jedno parsování pro všechny (toDate) a z něj odvozené formáty.
// Modul nic nezapisuje a formát uložených polí nemění („V této fázi nesahat");
// nové zápisy data mají používat toIsoDate().
//
// Datum bez času ("YYYY-MM-DD", "D. M. YYYY") se bere jako MÍSTNÍ půlnoc –
// new Date("2026-10-02") by dal půlnoc UTC a v záporných časových pásmech
// předchozí den. Pozn.: utils/pricing.ts má vlastní toDateSafe (zamčený
// soubor, 1:1 s původní cenovou logikou) – pro Timestamp/Date/ISO s časem dává
// stejný výsledek, české zápisy neumí.

/** "D. M. YYYY", "DD.MM.YYYY", "2.10.2026", volitelně s časem "HH:mm[:ss]". */
const CZ_DATE =
  /^(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/;

/** "YYYY-MM-DD" (jen datum). */
const ISO_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/** "YYYY-MM-DDTHH:mm[:ss[.sss]][Z|±hh:mm]" (oddělovač T nebo mezera). */
const ISO_DATE_TIME =
  /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?(?:Z|[+-]\d{2}:?\d{2})?$/;

const isValidDate = (d: Date): boolean => !Number.isNaN(d.getTime());

const validOrNull = (d: Date): Date | null => (isValidDate(d) ? d : null);

const pad2 = (n: number): string => String(n).padStart(2, "0");

/**
 * Místní datum z rozložených částí; přetečení ("31. 2. 2026", "25:00") je
 * chyba v datech, ne datum o měsíc dál – vrací null.
 */
function localDate(
  year: number,
  month: number,
  day: number,
  hours = 0,
  minutes = 0,
  seconds = 0
): Date | null {
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;
  if (hours > 23 || minutes > 59 || seconds > 59) return null;
  const d = new Date(year, month - 1, day, hours, minutes, seconds);
  return d.getFullYear() === year &&
    d.getMonth() === month - 1 &&
    d.getDate() === day
    ? d
    : null;
}

function parseDateString(value: string): Date | null {
  const s = value.trim();
  if (!s) return null;

  const cz = CZ_DATE.exec(s);
  if (cz) {
    return localDate(
      Number(cz[3]),
      Number(cz[2]),
      Number(cz[1]),
      cz[4] ? Number(cz[4]) : 0,
      cz[5] ? Number(cz[5]) : 0,
      cz[6] ? Number(cz[6]) : 0
    );
  }

  const iso = ISO_DATE.exec(s);
  if (iso) return localDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  if (ISO_DATE_TIME.test(s)) {
    // Bez posunu = místní čas, se "Z"/posunem podle něj (chování Date).
    return validOrNull(new Date(s.replace(" ", "T")));
  }

  // Ostatní zápisy (RFC 2822, "Oct 2 2026") jen když obsahují písmena. Čistě
  // číselné řetězce v jiném pořadí ("10/02/2026", "2.10.26") by prohlížeč
  // hádal po americku – radši nic.
  return /[a-zA-Z]/.test(s) ? validOrNull(new Date(s)) : null;
}

/**
 * Datum z čehokoli, co se v datech vyskytuje: Date, Firestore Timestamp
 * (toDate), { seconds[, nanoseconds] }, number (ms od epochy), ISO
 * "YYYY-MM-DD" / "YYYY-MM-DDTHH:mm[:ss]", české "D. M. YYYY" / "DD.MM.YYYY"
 * (i s mezerami, volitelně s časem). Nečitelné, prázdné nebo neplatné
 * (31. 2.) → null. Vrací vždy novou instanci.
 */
export function toDate(raw: unknown): Date | null {
  if (raw === null || raw === undefined || raw === "") return null;
  if (raw instanceof Date) {
    return isValidDate(raw) ? new Date(raw.getTime()) : null;
  }
  if (typeof raw === "number") {
    return Number.isFinite(raw) ? validOrNull(new Date(raw)) : null;
  }
  if (typeof raw === "string") return parseDateString(raw);
  if (typeof raw === "object") {
    const o = raw as {
      toDate?: unknown;
      seconds?: unknown;
      nanoseconds?: unknown;
    };
    if (typeof o.toDate === "function") {
      try {
        const d = (o.toDate as () => unknown)();
        return d instanceof Date && isValidDate(d) ? new Date(d.getTime()) : null;
      } catch {
        return null;
      }
    }
    if (typeof o.seconds === "number" && Number.isFinite(o.seconds)) {
      const nanos =
        typeof o.nanoseconds === "number" && Number.isFinite(o.nanoseconds)
          ? o.nanoseconds
          : 0;
      return validOrNull(new Date(o.seconds * 1000 + Math.floor(nanos / 1e6)));
    }
  }
  return null;
}

/** "YYYY-MM-DD" podle místního času (formát races.date a nově i payments.date); nečitelné → "". */
export function toIsoDate(raw: unknown): string {
  const d = toDate(raw);
  if (!d) return "";
  return `${String(d.getFullYear()).padStart(4, "0")}-${pad2(
    d.getMonth() + 1
  )}-${pad2(d.getDate())}`;
}

/** "2. 10. 2026" (jako toLocaleDateString("cs-CZ"), ale stejně v každém prostředí); nečitelné → "". */
export function formatCzDate(raw: unknown): string {
  const d = toDate(raw);
  if (!d) return "";
  return `${d.getDate()}. ${d.getMonth() + 1}. ${d.getFullYear()}`;
}

/**
 * "02.10.2026" – zápis data z bankovního výpisu (tak ho do payments.date ukládá
 * párovací Cloud Function – 1046 z 1058 dokumentů v produkci, ověřeno 3. 10. 2026).
 * Ruční platby ho používají taky, ať pole date nemá formát, který funkce nezná.
 * Nečitelné → "".
 */
export function formatStatementDate(raw: unknown): string {
  const d = toDate(raw);
  if (!d) return "";
  const pad = (n: number) => (n < 10 ? "0" + n : String(n));
  return `${pad(d.getDate())}.${pad(d.getMonth() + 1)}.${d.getFullYear()}`;
}

/** "2. 10. 2026 14:05" (volitelně "14:05:33"); nečitelné → "". */
export function formatCzDateTime(raw: unknown, withSeconds = false): string {
  const d = toDate(raw);
  if (!d) return "";
  const time = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return `${formatCzDate(d)} ${withSeconds ? `${time}:${pad2(d.getSeconds())}` : time}`;
}

/** Milisekundy pro řazení; nečitelné → 0 (stejná sémantika jako useRaces.toMillis). */
export function sortableDate(raw: unknown): number {
  const d = toDate(raw);
  return d ? d.getTime() : 0;
}

/** Rok data (místní čas); nečitelné → null. Náhrada za extractYearFromDate / getYearFromDate obrazovek plateb. */
export function dateYear(raw: unknown): number | null {
  const d = toDate(raw);
  return d ? d.getFullYear() : null;
}

/** Stejný kalendářní den (místní čas); když se některé datum nepřečte → false. */
export function isSameDay(a: unknown, b: unknown): boolean {
  const da = toDate(a);
  const db = toDate(b);
  if (!da || !db) return false;
  return (
    da.getFullYear() === db.getFullYear() &&
    da.getMonth() === db.getMonth() &&
    da.getDate() === db.getDate()
  );
}

/** Půlnoc místního času daného dne (porovnání „jen podle data", např. platnost ZP k závodu); nečitelné → null. */
export function startOfDay(raw: unknown): Date | null {
  const d = toDate(raw);
  if (!d) return null;
  d.setHours(0, 0, 0, 0);
  return d;
}
